import api from '@common/api';
import session from '@common/session';
import { DividendsV3, InvestorAccount, TransactionV2 } from '@services/appsync/types';
import { createStore, useStore } from 'zustand';
import { persist } from 'zustand/middleware';

interface TransactionsModelProps {
  dividends?: DividendsV3 | null;
  transactions?: TransactionV2[];
}
interface TransactionsModelActions {
  fetchDividends: () => Promise<DividendsV3 | null>;
  fetchTransactions: (investorAccount: InvestorAccount) => Promise<TransactionV2[] | null>;
  setTransactions: (transactions: TransactionV2[]) => void;
  setDividends: (dividends: DividendsV3 | null) => void;
}

export const TransactionsModel = createStore(
  persist<TransactionsModelProps & TransactionsModelActions>(
    (set) => ({
      fetchDividends: async () => {
        const investorID = session.get()?.investorId;
        if (!investorID) return null;
        const dividends = await api.allDividendsV3({ investor_account_id: investorID });
        set({ dividends });
        return dividends;
      },
      fetchTransactions: async (investorAccount) => {
        const transactions = await api.allTransactionsV2({
          investor_account_id: investorAccount.id,
        });
        if (transactions) set({ transactions });
        return transactions ? transactions : null;
      },
      setDividends: (dividends) => {
        set({ dividends });
      },
      setTransactions: (transactions) => {
        set({ transactions });
      },
    }),
    {
      getStorage: () => localStorage,
      name: 'transactions-model',
    },
  ),
);

export function useTransactionsModel(): TransactionsModelProps & TransactionsModelActions;
export function useTransactionsModel<T>(
  selector: (state: TransactionsModelProps & TransactionsModelActions) => T,
  equals?: (a: T, b: T) => boolean,
): T;
export function useTransactionsModel<T>(
  selector?: (state: TransactionsModelProps & TransactionsModelActions) => T,
  equals?: (a: T, b: T) => boolean,
) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return useStore(TransactionsModel, selector!, equals);
}
